import React, { useEffect, useMemo, useRef } from 'react';
import { Container, Header, Modal } from '@amzn/awsui-components-react';
import { AIInsightsContextValues } from 'src/services/ai-insights/AIInsightsState';
import useAIInsightChat from './useAIInsightChat';
import AIInsightChatPanel from './AIInsightChatPanel';
import { AIInsightChatContext } from './AIInsightChatContext';
import { useAIInsightsContext } from './AIInsightsContext';
import './AIInsight.scss';

export interface AIInsightPanelProps {
    /** CLI / report the conversation is scoped to. */
    context: AIInsightsContextValues;
    /** Heading shown on the container or modal. */
    heading: string;
    /** Renders in the page flow instead of a modal. */
    inline?: boolean;
    /** Prompt sent as soon as the panel mounts. Shown in the chat as the user's message. */
    defaultPrompt?: string;
    /** Called when a modal panel is dismissed. Unused when inline. */
    onClose?: () => void;
}

/**
 * Owns one AI Insights conversation.
 *
 * Deliberately a separate component from AIInsightButton: the chat hook lives here, so it is
 * only mounted once the panel is opened. The variance report renders a trigger per row (page
 * sizes go up to 500) and idle rows must not each carry a chat hook, its state and its
 * cleanup effect.
 */
export default function AIInsightPanel({
    context,
    heading,
    inline,
    defaultPrompt,
    onClose,
}: AIInsightPanelProps) {
    const { aiInsightsService } = useAIInsightsContext();
    const chat = useAIInsightChat(context, aiInsightsService);

    // The panel mounts already open, so the opening prompt is sent from here rather than
    // from the trigger — that keeps the modal and inline entry paths identical.
    const startedRef = useRef(false);
    useEffect(() => {
        if (startedRef.current || !defaultPrompt) {
            return;
        }
        startedRef.current = true;
        chat.sendMessage(defaultPrompt);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultPrompt]);

    const chatValue = useMemo(() => ({
        messages: chat.messages,
        loading: chat.loading,
        error: chat.error,
        pollingReportId: chat.pollingReportId,
        pollingElapsed: chat.pollingElapsed,
        sendMessage: chat.sendMessage,
    }), [
        chat.messages,
        chat.loading,
        chat.error,
        chat.pollingReportId,
        chat.pollingElapsed,
        chat.sendMessage,
    ]);

    const chatPanel = (
        <AIInsightChatContext.Provider value={chatValue}>
            <AIInsightChatPanel />
        </AIInsightChatContext.Provider>
    );

    if (inline) {
        return (
            <Container header={<Header variant="h3">{heading}</Header>}>
                {chatPanel}
            </Container>
        );
    }

    return (
        <Modal
            visible
            onDismiss={onClose}
            size="max"
            header={heading}
            closeAriaLabel="Close AI Insights"
        >
            {chatPanel}
        </Modal>
    );
}
